import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Button } from "react-md";
import { Card, CardTitle, CardText } from "react-md/lib/Cards";
import DialogContainer from "react-md/lib/Dialogs";
import { getHoldReasons, putOnHold, putOffHold } from "requests/onHold";
import { CASE_HOLD_UPDATE } from "typeConstants/rights";
import Message from "components/Homeowner/CaseForms/Actions/OnHold/Mueller/CaseNote";
import { MSpinner, MField } from "components/Shared";
import { DATA_TYPE_DATE, DATA_TYPE_STRING } from "typeConstants/dataTypes";
import { CaseHoldDetailDto } from "interfaces/Mueller.Business.Models";
import {format} from "date-fns";

export const ON_HOLD_TAB_ID = "ON_HOLD_TAB_ID";
export const ON_HOLD_MESSAGE_TAB_ID = "ON_HOLD_MESSAGE_TAB_ID";

const MuellerOnHold = ({
  controlNumber,
  canUpdateOnHold,
  openCaseActionTab,
  closeTab,
  internetUserId,
}) => {
  const [fetchingHoldStatus, setFetchingHoldStatus] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [showOnHoldDialog, setShowOnHoldDialog] = useState(false);
  const [offHoldDate, setOffHoldDate] = useState(null);
  const [onHoldNote, setOnHoldNote] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    getHoldInfo();
    return () => {
      mounted.current = false;
    };
  }, []);

  const getHoldInfo = () => {
    setFetchingHoldStatus(true);
    getHoldReasons(controlNumber, handleStatus, handleStatusError, mounted);
  };

  const handleStatus = (response: CaseHoldDetailDto | null) => {
    if (response) {
      setIsOnHold(true);
      setOffHoldDate(response.endDate);
      setFetchingHoldStatus(false);
    }
    else {
      // Handle case where response is null
      setIsOnHold(false);
      setOffHoldDate(undefined);
      setFetchingHoldStatus(false);
    }
  };
  const handleStatusError = () => setFetchingHoldStatus(false);

  const handleOnHoldClick = () => {
    if (isOnHold) {
      putOffHold(controlNumber, internetUserId, onHoldChangedCallback, onHoldChangedCallback, mounted);
    } else {
      putOnHold(controlNumber, internetUserId, offHoldDate, onHoldNote, onHoldChangedCallback, onHoldChangedCallback, mounted);
    }
  };

  const onHoldChangedCallback = () => {
    setFetchingHoldStatus(true);
    getHoldInfo();
  };

  const toggleExpanded = () => setExpanded(!expanded);

  const sendOnHoldActions = [
    <Button
      className="m-t-10"
      key="continue"
      raised
      primary
      onClick={() => {
        setShowOnHoldDialog(false);
        handleOnHoldClick();
      }}
    >
      Continue
    </Button>,
    <Button
      className="m-t-10"
      key="cancel"
      raised
      primary
      onClick={() => setShowOnHoldDialog(false)}
    >
      Cancel
    </Button>
  ];

  if (fetchingHoldStatus) {
    return <MSpinner />;
  }

  return (
    <>
      <Card
        className="md-cell md-cell--12 case-header"
        expanded={expanded}
        onExpanderClick={toggleExpanded}
      >
        <CardTitle
          title="Mueller On Hold Actions"
          onClick={toggleExpanded}
          expander
        />
        <h4>
          <b>Status: </b> {`${isOnHold ? "On Hold till " + (offHoldDate ? format(offHoldDate, "MM/DD/YYYY") : "[Unknown]") : "Off Hold"} `}
          <i
            className="md-icon material-icons right"
            title="Refresh Status"
            onClick={getHoldInfo}
          >
            refresh
          </i>
        </h4>
        <CardText expandable>
          <span className={"case-action"}>
            <Button
              id="on-hold-action-button"
              raised
              primary
              onClick={() => setShowOnHoldDialog(true)}
              disabled={!canUpdateOnHold || isOnHold}
            >
              On Hold
          </Button>
          </span>
          <span className={"case-action"}>
            <Button
              id="off-hold-action-button"
              raised
              primary
              onClick={handleOnHoldClick}
              disabled={!canUpdateOnHold || !isOnHold}
            >
              Off Hold
          </Button>
          </span>
        </CardText>
      </Card>
      <div id="off-hold-date-container">
        <DialogContainer
          id="dialog-off-hold-date"
          className="dialog-off-hold-date"
          visible={showOnHoldDialog}
          title={"Set Case On-Hold"}
          modal
          width={500}
          actions={sendOnHoldActions}
        >
          <div>Set Off-Hold Date? (optional)</div>
          <MField
            key={"update-off-hold-date"}
            id={"update-off-hold-date"}
            dataType={DATA_TYPE_DATE}
            onChange={(_, i) => setOffHoldDate(i)}
          />
          <div>Set On-Hold Note (optional)</div>          
          <MField            
            key={"update-on-hold-note"}
            id={"update-on-hold-note"}
            dataType={DATA_TYPE_STRING}
            onChange={(_, i) => setOnHoldNote(i)}
          />          
        </DialogContainer>
      </div>
    </>
  );
};

MuellerOnHold.propTypes = {
  controlNumber: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  caseHeader: PropTypes.object,
  canUpdateOnHold: PropTypes.bool,
  onHoldReasons: PropTypes.array,
  isFetchingHoldReasons: PropTypes.bool,
  openCaseActionTab: PropTypes.func, // Tab control method used to open a new tab
  closeTab: PropTypes.func,  // Tab control method that is called with a tab id to close the tab
  internetUserId: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.number
  ]),
};

const mapStateToProps = (state) => {
  return {
    controlNumber: state.caseDetails.controlNumber,
    caseHeader: state.caseForms.caseHeader,
    canUpdateOnHold: state.user.profile.rights.includes(CASE_HOLD_UPDATE),
    internetUserId: state.user.profile.internetUserId,
  };
};

export default connect(mapStateToProps)(MuellerOnHold);
